import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useStatus, SUCCESS, ERROR } from '@rootstrap/redux-tools';
import { useAnalytics, useNavigation, useToast } from 'hooks';
import { useLogger } from 'components/common/LogViewer';
import capitalize from 'lodash/capitalize';
import { editSeries, getTrailerUrlForJWPlayer } from 'state/actions/seriesActions';
import { parseHashtags, routeWithProps, isValidURL, log } from 'utils/helpers';
import { clearEpisodes } from 'state/actions/episodesActions';
import { routesPaths } from 'constants/routesPaths';
import parseError from 'utils/parseError';
import UserService from 'services/userService';
import { MIXPANEL_EVENTS, MIXPANEL_PARAMS } from 'constants/constants';
import axios from 'axios';
import SeriesService from 'services/seriesService';
import useFileUpload from 'hooks/common/useFileUpload';

export default () => {
  const { trackEvent } = useAnalytics();
  const dispatch = useDispatch();
  const { goTo } = useNavigation();
  const username = useRef();
  const [loading, setLoading] = useState(false);
  const { showErrorToast, showToast } = useToast();
  const [series, setSeries] = useState({});
  const [seriesId, setSeriesId] = useState(null);
  const { status, error } = useStatus(editSeries);
  const { trailerData } = useSelector(({ seriesUpload: { trailerData } }) => ({ trailerData }));
  const { episodes } = useSelector(({ episodes: { episodes } }) => ({ episodes }));
  const [completedVideos, setCompletedVideos] = useState(0);
  const [uploadedVideos, setUploadedVideos] = useState(0);
  const [hasVideosToUpload, setHasVideosToUpload] = useState(false);
  const totalVideoRef = useRef();
  const videoDoneRef = useRef();
  const totalVideos = episodes.length + (!isValidURL(series.trailer) ? 1 : 0);
  const { status: getTrailerUrlStatus, error: getTrailerUrlError } = useStatus(
    getTrailerUrlForJWPlayer
  );

  // Initialize logger
  const {
    logs,
    showLogs,
    setShowLogs,
    logInfo,
    logWarning,
    logError,
    logApiError,
    clearLogs
  } = useLogger();

  // Initialize file upload hook
  const {
    uploadFile,
    uploadProgress,
    uploadStage,
    currentFileName,
    setUploadStage,
    setUploadProgress,
    setCurrentFileName
  } = useFileUpload({
    onUploadProgress: progress => {
      setUploadProgress(progress);
    },
    onUploadComplete: () => {
      setUploadStage('processing');
    },
    onUploadError: error => {
      log('error', 'Error uploading file', {}, error);
      setUploadStage('');
    },
    getMultipartUploadUrl: (uploadId, mediaId, partNumber) =>
      SeriesService.getMultipartUploadUrl(uploadId, mediaId, partNumber),
    completeMultipartUpload: (uploadId, mediaId, parts) =>
      SeriesService.completeMultipartUpload(uploadId, mediaId, parts),
    onRedirect: id => navigateToEditSeries(id),
    redirectId: seriesId
  });

  // Clear any existing error states when the component is mounted or unmounted
  useEffect(() => {
    // Reset error states on mount
    resetDispatchData();

    // Reset error states on unmount
    return () => {
      resetDispatchData();
    };
  }, []);

  // Add a function to handle navigation to edit series page
  const navigateToEditSeries = id => {
    if (id) {
      // Get the celebrityId from the username reference if available
      const celebrityId = username.current ? username.current.toLowerCase() : '';
      logInfo(
        `Refreshing page and redirecting to edit originals for series ID: ${id}, celebrity ID: ${celebrityId}`
      );

      // Show a toast message before redirecting
      showErrorToast(
        'Upload process interrupted. Redirecting to edit page where you can try again...'
      );

      // Add a short delay to ensure the toast is visible before redirect
      setTimeout(() => {
        // Use window.location.href to force a full page refresh instead of React navigation
        window.location.href = `/edit-originals/${id}/${celebrityId}`;
      }, 1500);
    } else {
      logError('Cannot redirect to edit originals page: No series ID available');
      showErrorToast('Error: Could not find series ID for redirect.');
    }
  };

  // Update seriesId when series object changes and has an id
  useEffect(() => {
    if (series && series.id && !seriesId) {
      logInfo(`Setting seriesId from series object: ${series.id}`);
      setSeriesId(series.id);
    }
  }, [series, seriesId]);

  const editNewSeries = async (seriesData = null) => {
    // Use the passed seriesData if provided, otherwise use the state
    const currentData = seriesData || series;

    logInfo('Preparing to edit series', {
      seriesId: currentData.id,
      totalEpisodes: episodes.length + (currentData.episodes?.length || 0),
      usingPassedData: !!seriesData
    });

    const { categoryIds = [], description, hashtags = [], title, trailer, id } = currentData;

    // Validate ID and set seriesId immediately
    if (!id) {
      logError('Missing series ID', { id });
      showErrorToast('Error: Missing series ID.');
      setLoading(false);
      return;
    } else if (id !== seriesId) {
      logInfo(`Updating seriesId from ${seriesId} to ${id}`);
      setSeriesId(id);
    }

    // For edit series, we might have different trailer scenarios:
    // 1. URL string (existing trailer) - isValidURL(trailer) will be true
    // 2. File object (new trailer) - will need trailerData or be multipart
    // 3. Multipart upload object - will have trailer.isMultipart and trailer.mediaId
    if (!isValidURL(trailer) && !trailer?.isMultipart && !trailerData) {
      logError('Invalid trailer data for edit', {
        trailer,
        isURL: isValidURL(trailer),
        isMultipart: !!trailer?.isMultipart,
        hasTrailerData: !!trailerData
      });
      showErrorToast('Error with trailer data. Please try again.');
      setLoading(false);
      return;
    }

    const data = {
      id,
      description,
      title,
      banner_media_id: trailer?.isMultipart
        ? trailer?.mediaId
        : trailerData
        ? trailerData?.mediaId
        : '',// Only use trailerData.mediaId for new uploads
      mime_type: trailerData ? trailer?.type : currentData.mimeType
    };

    // For edits, banner_media_id can be empty if using existing trailer
    if (!isValidURL(trailer) && !data.banner_media_id) {
      logError('Missing banner_media_id for new trailer', {
        isMultipart: !!trailer?.isMultipart,
        trailerMediaId: trailer?.mediaId,
        trailerDataMediaId: trailerData?.mediaId,
        isURL: isValidURL(trailer)
      });
      showErrorToast('Error: Failed to get media ID for trailer. Please try again.');
      setLoading(false);
      return;
    }

    logInfo('Building episodes list for edit');

    // Get unique episodes by mediaId to prevent duplicates
    const existingMediaIds = new Set();
    const uniqueEpisodes = [];

    // First add existing episodes from seriesData
    if (currentData.episodes && currentData.episodes.length > 0) {
      currentData.episodes.forEach(episode => {
        existingMediaIds.add(episode.mediaId);
        uniqueEpisodes.push(episode);
      });
    }

    // Then add episodes from the state (new ones being added)
    episodes.forEach(episode => {
      if (!existingMediaIds.has(episode.mediaId)) {
        existingMediaIds.add(episode.mediaId);
        uniqueEpisodes.push(episode); // This will add new episodes to the end
      } else {
        logWarning(`Skipping episode with duplicate mediaId: ${episode.mediaId}`);
      }
    });

    logInfo(`Prepared ${uniqueEpisodes.length} unique episodes for submission`);

    const episodesList = uniqueEpisodes.map(
      (
        {
          creditsStartTime,
          description,
          earlyReleaseDateTime,
          exclusive,
          mediaId,
          membersPrice,
          nonMembersPrice,
          releaseDateTime,
          title,
          seriesVideo,
          content_viewer_category,
          lead_cast,
          credits,
          episodeId,
          notify_subscribers,
          duration
        },
        index
      ) => ({
        credits_start_time: creditsStartTime,
        early_release_date_time: earlyReleaseDateTime,
        media_id: seriesVideo?.isMultipart ? seriesVideo?.mediaId : mediaId,
        members_price: membersPrice === 0 ? null : Number(membersPrice).toFixed(2),
        non_members_price: nonMembersPrice === 0 ? null : Number(nonMembersPrice).toFixed(2),
        release_date_time: releaseDateTime,
        position: index,
        episode_id: episodeId || null,
        mime_type: seriesVideo?.type || 'video/mp4',
        credits,
        content_viewer_category,
        description,
        exclusive,
        title,
        lead_cast,
        notify_subscribers,
        duration: duration || '00:00:00'
      })
    );

    logInfo('Creating FormData for series edit', {
      categoryCount: categoryIds?.length || 0,
      hashtagCount: hashtags?.length || 0,
      episodesCount: episodesList?.length || 0
    });

    // Add additional debug logging to show the episodes data being sent
    logInfo(
      'Episodes data being sent:',
      episodesList.map(ep => ({
        mediaId: ep.media_id,
        episodeId: ep.episode_id,
        position: ep.position,
        title: ep.title
      }))
    );

    const formData = new FormData();
    Object.keys(data).forEach(key => {
      formData.append(`series[${key}]`, data[key]);
    });

    formData.append('series[logo_image]', currentData.logo_image);
    formData.append('series[desktop_image]', currentData.desktop_image);
    formData.append('series[mobile_image]', currentData.mobile_image);

    formData.append('series[category_ids]', JSON.stringify(categoryIds || []));
    formData.append('series[hashtags]', JSON.stringify(hashtags || []));
    formData.append('series[episodes]', JSON.stringify(episodesList || []));

    formData.append('series[lead_cast]', currentData.lead_cast || '');
    formData.append('series[content_viewer_category]', currentData.content_viewer_category || '');
    formData.append('series[notify_subscribers]', currentData.notify_subscribers || false);
    formData.append('series[credits]', currentData.credits || '');
    // formData.append('series[season]', series.season);

    logInfo('Dispatching editSeries action', { seriesId: currentData.id });
    const response = await dispatch(editSeries(formData, currentData.id));

    // Store the series ID for possible redirects after failed uploads
    if (currentData.id) {
      setSeriesId(currentData.id);
    }
  };

  useEffect(() => {
    // Only proceed if we have a valid series ID
    if (!series.id) {
      return;
    }

    if (getTrailerUrlStatus === SUCCESS) {
      logInfo('Successfully obtained trailer URL for edit', { trailerData });
      // Keep the series state as backup but use the direct reference
      const currentSeriesData = { ...series };
      editNewSeries(currentSeriesData);
      return;
    }

    if (getTrailerUrlStatus === ERROR) {
      logError('Error getting trailer URL for edit', { error: getTrailerUrlError });
      showErrorToast(getTrailerUrlError);
      dispatch(getTrailerUrlForJWPlayer.reset());
      setLoading(false);
    }
  }, [getTrailerUrlStatus]);

  // Add validation function to be called before proceeding with edit
  const validateBeforeEdit = data => {
    logInfo('Validating series data before edit', {
      hasTrailer: !!data.trailer || isValidURL(data.trailer),
      hasLogo: !!data.logo_image || typeof data.logo_image === 'string',
      hasDesktopImage: !!data.desktop_image || typeof data.desktop_image === 'string',
      hasMobileImage: !!data.mobile_image || typeof data.mobile_image === 'string',
      categoryCount: data.categoryIds?.length
    });

    // Improved category validation with more detailed logging
    if (!data.categoryIds || !Array.isArray(data.categoryIds) || data.categoryIds.length === 0) {
      logError('No categories selected', {
        categoryIds: data.categoryIds,
        type: typeof data.categoryIds,
        isArray: Array.isArray(data.categoryIds),
        length: Array.isArray(data.categoryIds) ? data.categoryIds.length : null
      });
      showErrorToast('Please select at least one category');
      return false;
    }

    logInfo('Validation passed');
    return true;
  };

  const onSubmit = useCallback(
    data => {
      logInfo('Starting series edit submission', {
        title: data.title,
        id: data.id
      });

      // Store series ID immediately when submitting
      if (data.id) {
        setSeriesId(data.id);
        logInfo(`Setting seriesId state to ${data.id}`);
      }

      // Reset any previous error states before submitting
      dispatch(editSeries.reset());
      dispatch(getTrailerUrlForJWPlayer.reset());
      setUploadStage('');
      setUploadProgress(0);
      setCompletedVideos(0);
      setUploadedVideos(0);
      setHasVideosToUpload(false);

      // Set loading state immediately
      setLoading(true);
      
      // Initially assume we won't need video upload UI
      setHasVideosToUpload(false);
      
      // Check if we will need to upload videos during this edit
      const willUploadTrailer = data.trailer && !isValidURL(data.trailer) ? true : false;
      const willUploadEpisodes = episodes.length > 0;
      
      if (willUploadTrailer || willUploadEpisodes) {
        // Only set upload stage if we have videos to upload
        setUploadStage('validating');
      }

      // Safely map the values and filter out any undefined items
      const categoryIds = (data.categoryIds || [])
        .filter(item => item && typeof item === 'object' && 'value' in item)
        .map(item => item.value);

      logInfo('Processed category IDs', {
        categoryCount: categoryIds.length
      });

      data.hashtags = parseHashtags(data.description);
      data.categoryIds = categoryIds;

      // Validate before proceeding
      if (!validateBeforeEdit(data)) {
        setLoading(false);
        setUploadStage('');
        return;
      }

      // Update the state for non-critical code paths
      setSeries(data);
      
      // Store the trailer filename for display
      if (data.trailer) {
        if (typeof data.trailer === 'string') {
          setCurrentFileName(data.trailer);
        } else if (data.trailer.originalFile) {
          setCurrentFileName(data.trailer.originalFile.name);
        } else {
          setCurrentFileName(data.trailer.name);
        }
      }

      if (isValidURL(data.trailer) || data.trailer?.isMultipart) {
        logInfo(
          data.trailer?.isMultipart
            ? 'Using multipart upload for trailer'
            : 'Using existing trailer URL',
          {
            trailerUrl: data.trailer,
            isMultipart: !!data.trailer?.isMultipart,
            mediaId: data.trailer?.mediaId
          }
        );
        // If the trailer is a URL or multipart upload, we can use it directly
        editNewSeries(data);
      } else {
        logInfo('Getting new trailer URL for upload', { fileName: data.trailer?.name });
        // If the trailer is not a URL, we dispatch the action to upload it
        dispatch(getTrailerUrlForJWPlayer(data.trailer));
      }
    },
    [dispatch, logInfo, showErrorToast, validateBeforeEdit]
  );

  const resetDispatchData = () => {
    logInfo('Resetting dispatch data');
    dispatch(clearEpisodes());
    dispatch(editSeries.reset());
    dispatch(getTrailerUrlForJWPlayer.reset());
    // Clear error state
    setLoading(false);
    setUploadStage('');
    setHasVideosToUpload(false);
    clearLogs();
  };

  const onCompleteVideoUpload = () => {
    logInfo('Video upload process completed for edit');
    resetDispatchData();
    showToast('Publication successfully updated!');
    setLoading(false);
    setHasVideosToUpload(false);

    logInfo('Navigating to celebrity profile');

    // Replace with your desired URL pattern
    const profileUrl = `/id/${username.current.toLowerCase()}`;
    window.location.href = profileUrl;
  };

  const getProfile = async () => {
    try {
      logInfo('Fetching user profile');
      const { data } = await UserService.getProfile();
      username.current = data.user.username;
      logInfo('Profile fetched successfully', { username: data.user.username });
    } catch (error) {
      logApiError('Error fetching profile', error);
      throw parseError(error.response);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    if (completedVideos === totalVideos) {
      logInfo('All videos completed upload for edit', {
        completedVideos,
        totalVideos
      });
      // Show follow-up message after 20 seconds
      showToast('Processing your data. Please wait for few mins...');

      // Complete the upload after 5 seconds
      setTimeout(() => {
        onCompleteVideoUpload();
        videoDoneRef.current = 2;
      }, 5000);
    }
  }, [completedVideos]);

  const uploadVideosInJWPlayer = async videos => {
    try {
      log('info', 'Starting batch upload to JWPlayer', { videoCount: videos.length });
      videoDoneRef.current = 1;
      setUploadProgress(0);
      setUploadedVideos(0);
      totalVideoRef.current = videos.length;
      setUploadStage('validated');
      setHasVideosToUpload(true);

      for (let index = 0; index < videos.length; index++) {
        const video = videos[index];
        setUploadedVideos(index);

        log('info', `Starting upload for video ${index + 1} of ${videos.length}`, {
          videoIndex: index,
          totalVideos: videos.length
        });

        if (video.video) {
          if (typeof video.video === 'string') {
            setCurrentFileName(video.video);
          } else if (video.video.originalFile) {
            setCurrentFileName(video.video.originalFile.name);
          } else {
            setCurrentFileName(video.video.name);
          }
        }

        let uploadSuccess = true;
        if (video.video.isMultipart) {
          const { uploadId, mediaId, originalFile } = video.video;
          uploadSuccess = await uploadFile(null, originalFile, uploadId, mediaId);
        } else {
          const fileToUpload = video.video;
          uploadSuccess = await uploadFile(video.uploadUrl, fileToUpload);
        }

        if (uploadSuccess === false) {
          log('info', 'Upload failed, stopping batch process for redirection');
          return;
        }

        setCompletedVideos(prev => prev + 1);
        setUploadProgress(0);
        log('info', `Completed video ${index + 1} of ${videos.length}`);
      }

      log('info', 'All videos uploaded successfully');
      setUploadStage('completed');
    } catch (error) {
      log('error', 'Error in batch upload process', {}, error);
      showErrorToast('Something went wrong, try again please!');
      setUploadStage('');

      if (seriesId) {
        log('info', `Batch upload failed. Redirecting to edit series page: ${seriesId}`);
        navigateToEditSeries(seriesId);
      }
    }
  };

  useEffect(() => {
    if (status === SUCCESS) {
      logInfo('Series updated successfully, preparing to upload videos', {
        seriesId: seriesId || series?.id
      });

      // Make sure seriesId is set before proceeding with uploads
      if (!seriesId && series && series.id) {
        logInfo(`Setting seriesId to ${series.id} before handling uploads`);
        setSeriesId(series.id);
      }

      setLoading(true);
      trackEvent(MIXPANEL_EVENTS.uploadSeries, {
        [MIXPANEL_PARAMS.seriesName]: series.title
      });

      const videosToUpload = [];
      if (!isValidURL(series.trailer) && !series.trailer?.isMultipart) {
        logInfo('Adding trailer to upload queue', {
          trailerName: series.trailer?.name,
          mediaId: trailerData?.mediaId
        });

        videosToUpload.push({
          video: series.trailer,
          mediaId: trailerData?.mediaId,
          uploadUrl: trailerData?.uploadUrl
        });
      } else if (series.trailer?.isMultipart) {
        logInfo('Adding multipart trailer to upload queue', {
          trailerName: series.trailer?.name,
          mediaId: series.trailer?.mediaId,
          uploadId: series.trailer?.uploadId
        });

        videosToUpload.push({
          video: series.trailer,
          mediaId: series.trailer?.mediaId,
          uploadUrl: null
        });
      }

      episodes.forEach(({ mediaId, uploadUrl, seriesVideo }) => {
        logInfo('Adding episode to upload queue', {
          videoName: seriesVideo?.name,
          mediaId,
          isMultipart: !!seriesVideo?.isMultipart
        });

        videosToUpload.push({
          video: seriesVideo,
          mediaId,
          uploadUrl
        });
      });

      if (videosToUpload.length === 0) {
        logInfo('No videos to upload, completing process');
        setHasVideosToUpload(false);
        // Show success toast immediately when there are no videos to upload
        showToast('Publication successfully updated!');
        // Set loading to false since we're done
        setLoading(false);
        // Redirect after a short delay
        setTimeout(() => {
          logInfo('Navigating to celebrity profile after successful edit without uploads');
          const profileUrl = `/id/${username.current.toLowerCase()}`;
          window.location.href = profileUrl;
        }, 2000);
      } else {
        logInfo('Starting video upload process', {
          count: videosToUpload.length,
          hasMultipartTrailer: !!series.trailer?.isMultipart
        });
        setHasVideosToUpload(true);
        uploadVideosInJWPlayer(videosToUpload);
      }
    } else if (status === ERROR) {
      logError('Error updating series', { error });
      showErrorToast(error);
    }
  }, [status, dispatch]);

  // Add an error recovery method that users can call
  const retrySubmission = () => {
    logInfo('Retrying submission after error');
    resetDispatchData();
  };

  return {
    onSubmit,
    status,
    loading,
    uploadProgress,
    uploadedVideos,
    error: capitalize(error),
    totalVideos: totalVideoRef.current,
    uploadingStatus: videoDoneRef.current,
    uploadStage,
    logs,
    showLogs,
    setShowLogs,
    seriesId,
    currentFileName,
    hasVideosToUpload
  };
};
